import React, { useState } from 'react';
import { createInputId } from '../../utils/utilities';
import ApiNameSearch from "../NameSearch/ApiNameSearch";
import * as analytics from "../../analytics";
import TextInput from '../Inputs/TextInput';
import Button from '../Inputs/Button';
import LocationPinIcon from '../vectors/LocationPinIcon';
import SearchIcon from '../vectors/SearchIcon';
import LoadingSpinner from '../vectors/LoadingSpinner';
import ToggleGroup from '../Inputs/ToggleGroup';
import Tooltip from '../Tooltip';
import {CONTACT, FINANCIAL_ADVISER_ID} from "../../__constants__";

export function SearchBar(props) {
    const [locationView, setLocationView] = useState(props.searchType === 'location')
    const [location, setLocation] = useState(props.location)
    const [firstSearchByNameOption, setFirstSearchByNameOption] = useState(null)
    const [isRedirectingToProfile, setIsRedirectingToProfile] = useState(false)
    const [showMe, setShowMe] = useState(props.showMe)

    const handleViewToggle = () => {
        setLocationView(!locationView)
    }

    const handleLocationChange = (newLocation) => {
        setLocation(newLocation)
    }

    const handleFirstSearchByNameOptionChange = (value) => {
        setFirstSearchByNameOption(value)
    };

    const handleSetLoading = (loading) => {
        setIsRedirectingToProfile(loading)
    };

    const handleSubmit = e => {
        e.preventDefault();

        if (locationView) {
            props.handleSubmitLocationSearch(location);
        } else {
            if (firstSearchByNameOption) {
                setIsRedirectingToProfile(() => {
                    props.handleSubmitNameSearch(firstSearchByNameOption)
                    return true
                })
            }
        }
    }

    const handleShowMeToggle = (e, newValue) => {
        if (newValue !== null) {
            setShowMe(() => {
                analytics.track( 'search_view', {category: newValue === "advisers" ? 'toggled_adviser' : 'toggled_firm'});
                props.handleShowMeToggle(newValue)
                return newValue
            })
        }
    }

    const firmTooltipText = "You can choose to view individual advisers, or switch to view advisory businesses which will each include one or more advisers.";

    return (
        <form onSubmit={handleSubmit}>
            <div className='flex flex-col sm:flex-row box-border px-4 py-6 bg-[#DDE9FF] w-[calc(100%+16px)] -m-2 mb-4 mt-4 sm:items-center'>
                <div className={'grow-0 max-w-full basis-full sm:max-w-[50%] sm:basis-[50%] flex flex-wrap'}>
                    <div className='text-center xs:text-left grow-0 mb-1 max-w-full basis-full m-0 box-border'>
                        <label className='text-primary-blue text-xs font-bold pl-2 uppercase'>
                            { locationView ? "Town / Postcode" : "Search by Adviser Or Firm Name"}
                        </label>
                    </div>

                    <div className='sm:hidden grow-0 max-w-full basis-full m-0 box-border block'>
                        {locationView &&
                            <TextInput
                                placeholder='Town / Postcode'
                                iconLeft={<SearchIcon />}
                                onChange={(e) => handleLocationChange(e.target.value)}
                                onBlur={handleSubmit}
                                value={location}
                                id={`${createInputId('location')}-sm`}
                                autoFocus={true}
                            />
                        }

                        {!locationView &&
                            <ApiNameSearch id={'search-for-adviser-by-name-sm'} small={true} onFirstOptionChange={handleFirstSearchByNameOptionChange} loading={isRedirectingToProfile} setLoading={handleSetLoading}/>
                        }
                        <div className='text-center xs:text-left sm:hidden pl-2 pt-1 w-full mt-2'>
                            <span data-test={`search-adviser-by-${locationView ? 'name' : 'location'}-sm`} onClick={handleViewToggle} className='pr-1 text-sm text-secondary-blue cursor-pointer underline'>
                                Search for an adviser or firm by { locationView ? 'name' : 'location' }
                            </span>
                        </div>
                    </div>

                    <div className='sm:flex hidden grow-0 max-w-[75%] basis-3/4 m-0 box-border'>
                        {locationView &&
                            <TextInput
                                placeholder='Town / Postcode'
                                iconRight={<LocationPinIcon />}
                                onChange={(e) => handleLocationChange(e.target.value)}
                                value={location}
                                id={`${createInputId('location')}-md`}
                                autoFocus={true}
                            />
                        }

                        {!locationView &&
                            <ApiNameSearch id={'search-for-adviser-by-name-md'} small={true} onFirstOptionChange={handleFirstSearchByNameOptionChange} loading={isRedirectingToProfile} setLoading={handleSetLoading}/>
                        }
                    </div>
                    <div className='sm:flex hidden grow-0 max-w-[25%] basis-1/4 m-0 box-border'>
                        <Button className='py-1.5 ml-2' dataTest='search-btn' disabled={isRedirectingToProfile} text={isRedirectingToProfile ? <LoadingSpinner /> : 'Search'} />
                    </div>
                    <div className='sm:flex hidden grow-0 max-w-full basis-full m-0 box-border pl-2 mt-6'>
                        <div>
                            <span data-test={`search-adviser-by-${locationView ? 'name' : 'location'}-md`} onClick={handleViewToggle} className='text-secondary-blue cursor-pointer text-lg underline leading-6'>
                                Search for an adviser or firm by { locationView ? 'name' : 'location' }
                            </span>
                        </div>
                    </div>
                </div>
                <div className={'grow-0 max-w-full basis-full sm:max-w-[50%] sm:basis-[50%] flex flex-col mt-8 sm:mt-0'}>
                    <p className="text-center xs:text-left text-[1.1rem] sm:text-[1.2rem] font-normal leading-[1.5] m-0 ml-2 max-xs:flex max-xs:gap-2 max-xs:flex-col">
                        <strong>Need help?</strong> <span>Let our team find the right adviser for you</span>
                    </p>
                    <h5 className="text-center xs:text-left font-bold text-[1.4rem] leading-[1.334] my-2 ml-2">
                        <a className="no-underline text-primary-blue" href={`tel:${CONTACT.ACCOUNTS_TEAM_NUMBER}`}>
                            {CONTACT.ACCOUNTS_TEAM_NUMBER}
                        </a>
                    </h5>
                    <p className="text-center xs:text-left leading-[1.5] md:text-normal m-0 ml-2">
                        Free to call, no obligation 100% impartial service
                    </p>
                </div>
            </div>
            {props.isIfa && (
                <div className='w-[calc(100% + 16px)] items-center flex flex-wrap box-border'>
                    <div
                        className='w-full items-center flex-row p-2 pl-0 border-0 m-0 sm:inline-flex hidden relative min-w-0 align-top'>
                        <div className='w-full items-center flex-row pb-3 border-0 m-0 inline-flex p-0 relative min-w-0'>
                            <label id="test-radio-buttons-show-me-label" className='text-secondary-blue text-[1.1rem] font-semibold p-0 pr-3 leading-4 cursor-text'>
                                Show me:
                            </label>
                            <ToggleGroup
                                value={showMe ? showMe : 'advisers'}
                                onChange={handleShowMeToggle}
                                leftOption='Advisers'
                                rightOption='Firms'
                                className='w-72'
                            />
                            <Tooltip content={firmTooltipText}/>
                        </div>
                    </div>
                    <div className='w-full mt-4 grow-0 max-w-full basis-full m-0 box-border sm:hidden flex flex-col'>
                        <label id="test-radio-buttons-show-me-label" className='text-secondary-blue text-[1.1rem] font-semibold p-0 pr-3 pb-2 leading-4 cursor-text'>
                            Show me: 
                        </label>
                        
                        <div className='w-full items-center flex-row pb-3 border-0 m-0 inline-flex p-0 relative min-w-0'>
                            <ToggleGroup
                                value={showMe ? showMe : 'advisers'}
                                onChange={handleShowMeToggle}
                                leftOption='Advisers'
                                rightOption='Firms'
                                className='w-full'
                            />
                        </div>
                    </div>
                </div>
            )}
        </form>
    );
}

SearchBar.defaultProps = {
    location: '',
    searchType: 'location',
}

export default SearchBar;
